import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, ClipboardList, PhoneCall, Rocket } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Field, FormSuccess, inputCls, SubmitButton } from "@/components/forms";
import { bookConsultation } from "@/lib/api";

const steps = [
  {
    icon: ClipboardList,
    title: "Tell us about your business",
    copy: "Share what you do, where you trade, and what's slowing you down.",
  },
  {
    icon: PhoneCall,
    title: "We call you back",
    copy: "A DAS consultant reaches out within one business day to dig into your needs.",
  },
  {
    icon: Rocket,
    title: "Get a tailored plan",
    copy: "Hardware, software and services — matched to your budget and growth plans.",
  },
];

const empty = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  service_interest: "POS Software & Hardware",
  preferred_date: "",
  message: "",
};

export default function Consultation() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await bookConsultation(form);
      setSent(true);
      toast.success("Consultation booked — we'll call you back soon.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="consultation-page">
      <PageHero
        overline="Book a Consultation"
        lines={["One conversation.", "A whole new toolkit."]}
        copy="Free, no-pressure consultation with our Johannesburg team. We'll match the right payments, POS and value-added services to your business."
        testid="consultation-hero"
      />

      <section className="mx-auto max-w-7xl px-6 pb-28 md:px-12">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-5">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div
                  data-testid={`consultation-step-${i}`}
                  className="flex gap-5 rounded-3xl border border-neutral-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-das-violet/40"
                >
                  <span className="grid h-13 w-13 shrink-0 place-items-center rounded-2xl bg-das-violet/10 p-3.5 text-das-deep">
                    <s.icon className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="font-display text-lg font-semibold tracking-tight text-das-ink">
                      <span className="mr-2 text-das-violet">0{i + 1}</span>
                      {s.title}
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{s.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="flex items-center gap-4 rounded-3xl bg-das-violet/5 p-7">
                <CalendarCheck className="h-8 w-8 shrink-0 text-das-deep" />
                <p className="text-sm leading-relaxed text-neutral-700">
                  <span className="font-bold text-das-ink">Free & flexible.</span> On-site, video or
                  phone — whatever suits your trading hours.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="rounded-[2rem] border border-neutral-200 bg-white p-8 shadow-ambient sm:p-12">
              {sent ? (
                <FormSuccess
                  title="Consultation booked!"
                  message="Your request is in. A DAS consultant will call you back within one business day to confirm your session."
                  onReset={() => {
                    setSent(false);
                    setForm(empty);
                  }}
                  testid="consultation-success-message"
                />
              ) : (
                <form onSubmit={onSubmit} className="space-y-6" data-testid="consultation-form">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Your Name" htmlFor="consult-name">
                      <input
                        id="consult-name"
                        data-testid="consultation-name-input"
                        required
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Thandi Mokoena"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Business Name" htmlFor="consult-business">
                      <input
                        id="consult-business"
                        data-testid="consultation-business-input"
                        required
                        value={form.business_name}
                        onChange={set("business_name")}
                        placeholder="Thandi's Spaza"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Email Address" htmlFor="consult-email">
                      <input
                        id="consult-email"
                        type="email"
                        data-testid="consultation-email-input"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@business.co.za"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Contact Number" htmlFor="consult-phone">
                      <input
                        id="consult-phone"
                        data-testid="consultation-phone-input"
                        required
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="+27 82 000 0000"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Service You're Interested In" htmlFor="consult-service">
                      <select
                        id="consult-service"
                        data-testid="consultation-service-select"
                        value={form.service_interest}
                        onChange={set("service_interest")}
                        className={inputCls}
                      >
                        {[
                          "POS Software & Hardware",
                          "Payment Gateway",
                          "Value-Added Services",
                          "Tailor-Made / Rental Solutions",
                          "Compliance & Training",
                          "Partnerships & Bulk Buying",
                          "Not sure yet — advise me",
                        ].map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Preferred Date (optional)" htmlFor="consult-date">
                      <input
                        id="consult-date"
                        type="date"
                        data-testid="consultation-date-input"
                        value={form.preferred_date}
                        onChange={set("preferred_date")}
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <Field label="Anything else we should know? (optional)" htmlFor="consult-message">
                    <textarea
                      id="consult-message"
                      data-testid="consultation-message-input"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="How you trade, how many staff, current setup…"
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                  <SubmitButton loading={loading} testid="consultation-submit-button">
                    Book My Consultation
                  </SubmitButton>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
